import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, CheckSquare, Plus, Send, Zap } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import GettingStarted from "@/components/common/GettingStarted";
import { KpiCard } from "@/components/common/KpiCard";
import { CountUp } from "@/components/common/Motion";
import { LoadingBlock, EmptyState } from "@/components/common/States";
import { InsightsCard } from "@/components/common/InsightsCard";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const NET_LABEL = { instagram: "IG", linkedin: "LI", facebook: "FB", x: "X" };
const STATUS_LABEL = {
  draft: "Draft", in_review: "In review", changes_requested: "Changes requested",
  approved: "Approved", scheduled: "Scheduled", publishing: "Publishing",
  published: "Published", failed: "Failed",
};
const STATUS_TONE = {
  draft: "bg-[#F6F8FA] text-[#43535E]", in_review: "bg-[#FFF7E6] text-[#8A6D00]",
  changes_requested: "bg-[#FFE6E6] text-[#B02328]", approved: "bg-[#E8FBF1] text-[#0B5D3C]",
  scheduled: "bg-[#EEF3FB] text-[#1747B0]", publishing: "bg-[#EEF3FB] text-[#1747B0]",
  published: "bg-[#E8FBF1] text-[#0B5D3C]", failed: "bg-[#FFE6E6] text-[#B02328]",
};

export default function Dashboard() {
  const { user, role, currentTenant } = useAuth();
  const [posts, setPosts] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [approvals, setApprovals] = useState(null);

  useEffect(() => {
    let dead = false;
    async function load() {
      try {
        const [p, a, ap] = await Promise.all([
          api.get("/posts?limit=8"),
          api.get("/analytics/overview"),
          api.get("/posts/approvals/queue"),
        ]);
        if (dead) return;
        setPosts(p.data.items);
        setAnalytics(a.data);
        setApprovals(ap.data.items);
      } catch (e) {
        if (!dead) { setPosts([]); setAnalytics({ kpis: {}, trend: [], per_network: [] }); setApprovals([]); }
      }
    }
    load();
    return () => { dead = true; };
  }, [currentTenant?.id]);

  const kpis = analytics?.kpis || {};

  return (
    <div>
      {/* Renders nothing once the required steps are done, so an established
          workspace never sees a setup card it has finished with. */}
      <GettingStarted />
      <PageHeader
        title={`Hi ${user?.name?.split(" ")[0] || "there"}`}
        subtitle={`Here's what's happening in ${currentTenant?.name || "your workspace"}.`}
        actions={
          <>
            <Link to="/app/calendar" data-testid="dashboard-view-calendar" className="rounded-full border border-[#E4E9EF] px-4 py-2 text-sm font-semibold text-[#0B2B33] hover:bg-[#F6F8FA]">
              <CalendarDays className="mr-2 -mt-0.5 inline h-4 w-4" /> View calendar
            </Link>
            <Link to="/app/composer" data-testid="dashboard-new-post" className="hs-btn-primary inline-flex items-center gap-2 text-sm">
              <Plus className="h-4 w-4" /> New post
            </Link>
          </>
        }
      />

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <KpiCard testid="kpi-scheduled" label="Scheduled" value={<CountUp to={kpis.posts_scheduled ?? 0} />} hint="Ready to go out" />
        <KpiCard testid="kpi-published" label="Published (30d)" value={<CountUp to={kpis.posts_published ?? 0} />} tone="positive" />
        <KpiCard testid="kpi-engagements" label="Engagements" value={<CountUp to={kpis.total_engagements ?? 0} format={numFmt} />} hint="Likes + comments + shares" />
        <KpiCard testid="kpi-reach" label="Reach" value={<CountUp to={kpis.total_reach ?? 0} format={numFmt} />} />
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <section className="rounded-xl border border-[#E4E9EF] bg-white p-5" data-testid="recent-posts-card">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-display text-lg font-semibold text-[#0B2B33]">Recent posts</h2>
              <Link to="/app/composer" className="text-sm font-semibold text-[#1F5FD8] hover:underline">Draft new post</Link>
            </div>
            {posts === null ? (
              <LoadingBlock rows={4} />
            ) : posts.length === 0 ? (
              <EmptyState icon={Send} title="No posts yet" body="Start your first draft to see it here." action={
                <Link to="/app/composer" className="hs-btn-primary text-sm">Create a post</Link>
              } />
            ) : (
              <ul className="divide-y divide-[#E4E9EF]">
                {posts.map((p) => (
                  <li key={p.id} className="flex items-center justify-between py-3">
                    <div className="min-w-0 flex-1">
                      <Link to={`/app/composer/${p.id}`} className="line-clamp-1 text-sm font-semibold text-[#0B2B33] hover:underline">{p.title}</Link>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-[#8A97A0]">
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_TONE[p.status] || "bg-[#F6F8FA]"}`}>{STATUS_LABEL[p.status] || p.status}</span>
                        <span>{(p.variants || []).map(v => NET_LABEL[v.network]).filter(Boolean).join(" · ") || "No networks"}</span>
                        {p.scheduled_at ? <span>· {new Date(p.scheduled_at).toLocaleString()}</span> : null}
                      </div>
                    </div>
                    <Link to={`/app/composer/${p.id}`} className="ml-3 rounded-full p-2 text-[#43535E] hover:bg-[#F6F8FA]" aria-label="Open">
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <div className="space-y-6">
          <section className="rounded-xl border border-[#E4E9EF] bg-white p-5" data-testid="approvals-card">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-display text-lg font-semibold text-[#0B2B33]">Awaiting review</h2>
              <Link to="/app/approvals" className="text-sm font-semibold text-[#1F5FD8] hover:underline">Open queue</Link>
            </div>
            {approvals === null ? <LoadingBlock rows={3} /> :
              approvals.length === 0 ? (
                <EmptyState icon={CheckSquare} title="Inbox zero" body="Nothing waiting for approval right now." />
              ) : (
                <ul className="space-y-2">
                  {approvals.slice(0, 5).map((p) => (
                    <li key={p.id} className="flex items-center justify-between rounded-lg bg-[#F6F8FA] px-3 py-2">
                      <div className="min-w-0">
                        <Link to={`/app/composer/${p.id}`} className="line-clamp-1 text-sm font-semibold text-[#0B2B33] hover:underline">{p.title}</Link>
                        <div className="text-xs text-[#8A97A0]">{(p.variants || []).map(v => NET_LABEL[v.network]).filter(Boolean).join(" · ")}</div>
                      </div>
                      <Link to="/app/approvals" className="text-xs font-semibold text-[#1F5FD8] hover:underline">Review</Link>
                    </li>
                  ))}
                </ul>
              )}
          </section>

          <section className="rounded-xl border border-[#E4E9EF] bg-white p-5">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-display text-lg font-semibold text-[#0B2B33]">Networks</h2>
              <Link to="/app/social" className="text-sm font-semibold text-[#1F5FD8] hover:underline">Manage</Link>
            </div>
            <div className="space-y-2">
              {(analytics?.per_network || []).map((row) => (
                <div key={row.network} className="flex items-center justify-between rounded-lg bg-[#F6F8FA] px-3 py-2">
                  <span className="text-sm font-semibold capitalize text-[#0B2B33]">{row.network}</span>
                  <span className="text-xs text-[#43535E]">{numFmt(row.reach)} reach · {numFmt(row.engagements)} eng</span>
                </div>
              ))}
              {(!analytics?.per_network || analytics.per_network.length === 0) && (
                <EmptyState title="No network data yet" body="Connect an account and publish to see numbers here." />
              )}
            </div>
          </section>

          {/* Learning Loop — top insight across the primary networks the tenant uses */}
          <div className="space-y-3" data-testid="dashboard-insights">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-semibold text-[#0B2B33]">Learning Loop</h2>
              <Link to="/app/campaigns" className="text-sm font-semibold text-[#1F5FD8] hover:underline">
                <Zap className="mr-1 -mt-0.5 inline h-4 w-4" /> New campaign
              </Link>
            </div>
            {["instagram", "linkedin"].map((n) => (
              <InsightsCard key={n} network={n} compact />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function numFmt(n) {
  if (n == null) return "0";
  if (n >= 1e6) return (n / 1e6).toFixed(1) + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(1) + "k";
  return String(n);
}
